import React, { useContext, useEffect, useState } from "react";
import { layoutCardRadius, layoutPillRadius } from "../theme/colors";
import { campaignsPathTo, providersPathTo } from "../utils/Api";
import { originParamsFromUser, parseProvidersListResponse, isOutOfDeliveryArea } from "../utils/providerListParams";
import OutOfDeliveryAreaPanel from "./building_blocks/OutOfDeliveryAreaPanel";
import { UserContext } from "../contexts/UserContext";
import { Avatar, Box, ButtonBase, InputBase, Typography, Badge, Stack } from "@mui/material";
import { alpha } from '@mui/material/styles';
import ProviderCard from "./building_blocks/ProviderCard";
import ProductCard from "./building_blocks/ProductCard";
import WelcomeCarousel from "./WelcomeCarousel";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from "react-router-dom";
import SnackbarContext from '../contexts/SnackbarContext';
import { apiBaseUrl } from "../utils/Api/Api";
import { getFavoriteProviders } from "../utils/providerFavoritesStorage";
import BuyAgainSession from "./BuyAgainSession";

/*Icons*/
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShareIcon from '@mui/icons-material/Share';
import SearchIcon from '@mui/icons-material/Search';
import { copyPageLink } from '../utils/copyPageLink';

const Home = () => {
  const [providers, setProviders] = useState([]);
  const navigate = useNavigate()
  const [defaultAddress, setDefaultAddress] = useState()
  const [hasItemsInCart, setHasItemsInCart] = useState(false);
  const [campaignShowcase, setCampaignShowcase] = useState(null);
  const [favoriteProviders, setFavoriteProviders] = useState([]);
  const [deliveryArea, setDeliveryArea] = useState(null);
  const { currentUser } = useContext(UserContext);
  const { openSnackbar } = useContext(SnackbarContext);

  useEffect(() => {
    const checkCartItems = () => {
      const cartProducts = JSON.parse(localStorage.getItem('shoppingCartProducts') || '[]');
      setHasItemsInCart(cartProducts.length > 0);
    };

    checkCartItems();

    window.addEventListener('storage', checkCartItems);

    return () => {
      window.removeEventListener('storage', checkCartItems);
    };
  }, []);

  useEffect(() => {
    const params = originParamsFromUser(currentUser);

    providersPathTo.list({ params }).then(result => {
      const { providers: list, deliveryArea: area } = parseProvidersListResponse(result.data);
      setProviders(list);
      setDeliveryArea(area);
    });
    setFavoriteProviders(getFavoriteProviders());
    campaignsPathTo.active()
      .then(result => setCampaignShowcase(result.data))
      .catch(() => setCampaignShowcase(null));

    if (currentUser?.default_address) {
      setDefaultAddress(currentUser.default_address)
    }
  }, [
    currentUser,
    currentUser?.default_address?.latitude,
    currentUser?.default_address?.longitude,
  ]);

  useEffect(() => {
    document.title = 'Home - TudoPets.app - Marketplace Pet de Curitiba';
  }, []);

  const handleCartClick = () => {
    if (hasItemsInCart) {
      navigate(`/shopping-cart`);
    } else {
      openSnackbar('Não há itens no carrinho', 'info');
    }
  };

  const handleShareClick = async () => {
    try {
      await copyPageLink();
      openSnackbar('Link copiado para a área de transferência', 'success');
    } catch {
      openSnackbar('Não foi possível copiar o link', 'error');
    }
  };

  const outOfDeliveryArea = isOutOfDeliveryArea(deliveryArea);
  const deliveryRadiusKm = deliveryArea?.radius_km ?? 30;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>

      {/*Endereço e Carrinho*/}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
        {/* Endereço */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
          <Typography variant="sectionTitle">Endereço</Typography>

          <ButtonBase data-no-shadow="true" onClick={() => navigate('/addresses', { state: { returnTo: '/home' } })}
            sx={{ display: 'flex', alignItems: 'center', borderRadius: layoutCardRadius, gap: 0.5, p: 0.5, alignSelf: 'stretch', cursor: 'pointer', color: 'text.primary' }}>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon />
              {!defaultAddress && (
                <Typography sx={{ fontFamily: 'Nunito', fontSize: '16px', fontWeight: 600, lineHeight: '16px' }}>
                  Adicionar endereço de entrega
                </Typography>
              )}
              {defaultAddress && (
                <Box>
                  <Typography sx={{ fontFamily: 'Nunito', fontSize: '16px', fontWeight: 600, lineHeight: '16px' }}>
                    {defaultAddress.logradouro}, {defaultAddress.numero}
                  </Typography>
                </Box>
              )}
            </Box>
            <ExpandMoreIcon />
          </ButtonBase>
        </Box>

        {/* Compartilhar + Carrinho com bolinha — bottom nav vem do RouterLayer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ButtonBase
            aria-label="Compartilhar"
            sx={{
              display: 'flex',
              p: 2,
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              borderRadius: '50%',
              bgcolor: 'surface.card',
              cursor: 'pointer',
            }}
            onClick={handleShareClick}
          >
            <ShareIcon sx={{ color: 'text.secondary', transform: 'translateX(-1px)' }} />
          </ButtonBase>
          <Badge
            variant="dot"
            color="error"
            invisible={!hasItemsInCart}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiBadge-dot': {
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                bgcolor: 'error.main',
                transform: 'translate(-20%, 20%)',
              }

            }}
          >
            <ButtonBase
              sx={{
                display: 'flex',
                p: 2,
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                borderRadius: '50%',
                bgcolor: 'surface.card',
                cursor: 'pointer',
              }}
              onClick={handleCartClick}
            >
              <ShoppingCartIcon sx={{ color: 'text.secondary' }} />
            </ButtonBase>
          </Badge>
        </Box>
      </Box>

      {/* Barra de Pesquisa */}
      <ButtonBase
        component="div"
        onClick={() => navigate('/search')}
        sx={{ display: 'flex', p: '4px 4px 4px 16px', justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch', borderRadius: layoutPillRadius, bgcolor: 'surface.card', cursor: 'pointer', width: '100%' }}
      >
        <InputBase
          readOnly
          sx={{ width: '100%', pointerEvents: 'none' }}
          placeholder="O que você procura hoje?"
        />
        <ButtonBase data-no-shadow="true" sx={{ display: 'flex', p: '13px', justifyContent: 'center', alignItems: 'center', gap: 1, bgcolor: 'surface.input', borderRadius: '50%' }}>
          <SearchIcon sx={{ color: 'text.primary' }} />
        </ButtonBase>
      </ButtonBase>

      {/* Carrrossel */}
      <Box sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <WelcomeCarousel />
      </Box>

      {campaignShowcase?.products?.length > 0 && !outOfDeliveryArea && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, alignSelf: 'stretch' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
            <Typography variant="sectionTitle"><b>{campaignShowcase.campaign.name}</b></Typography>
            <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '21px', color: 'text.secondary' }}>
              Produtos em destaque das lojas participantes.
            </Typography>
          </Box>

          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
            }}
          >
            <Stack
              direction="row"
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 1,
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
                whiteSpace: 'nowrap',
                pb: 0.5,
                boxSizing: 'border-box',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': { height: '8px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)' },
                '&::after': {
                  content: '""',
                  flex: '0 0 32px',
                },
              }}
            >
              {campaignShowcase.products.map((product) => (
                <ButtonBase
                  key={product.id}
                  onClick={() => navigate(`/provider/${product.provider_id}?promo=1`)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    flex: '0 0 auto',
                    width: '172.5px',
                    minWidth: '172.5px',
                    textAlign: 'left',
                    borderRadius: layoutCardRadius,
                    overflow: 'hidden',
                  }}
                >
                  <ProductCard
                    product={product}
                    variant="whiteCard"
                    detailMode="weight"
                    sx={{ width: '100%', minWidth: '100%', boxSizing: 'border-box' }}
                  />
                </ButtonBase>
              ))}
            </Stack>
          </Box>
        </Box>
      )}

      {favoriteProviders.length > 0 && !outOfDeliveryArea && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            alignSelf: 'stretch',
            px: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, alignSelf: 'stretch' }}>
            <Typography variant="sectionTitle">Meus Favoritos</Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              alignSelf: 'stretch',
              overflowX: 'auto',
              overflowY: 'hidden',
              flexWrap: 'nowrap',
              py: 0.25,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {favoriteProviders.map((provider) => (
              <ButtonBase
                key={provider.id}
                data-no-shadow="true"
                onClick={() => navigate(`/provider/${provider.id}`)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: '0 0 86px',
                  width: '86px',
                  minWidth: '86px',
                  gap: 0.75,
                  borderRadius: layoutCardRadius,
                  p: 0.25,
                  textAlign: 'center',
                  color: 'text.primary',
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
                    boxShadow: (theme) => `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.16)}`,
                  }}
                >
                  <Avatar
                    src={provider.business_logo && `${apiBaseUrl}${provider.business_logo}`}
                    sx={{
                      width: 60,
                      height: 60,
                      border: '3px solid',
                      borderColor: 'background.paper',
                      bgcolor: 'surface.input',
                      boxShadow: (theme) => `0 4px 10px ${alpha(theme.palette.text.primary, 0.14)}`,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    width: '100%',
                    fontFamily: 'Nunito',
                    fontSize: '14px',
                    lineHeight: '17px',
                    fontWeight: 700,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    minHeight: '34px',
                  }}
                >
                  {provider.name}
                </Typography>
              </ButtonBase>
            ))}
          </Box>
        </Box>
      )}

      {/* <BuyAgainSession /> Desativado temporariamente */}

      {/* Categorias */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, alignSelf: 'stretch' }}>
        {!outOfDeliveryArea && (
          <Typography variant="sectionTitle">Todas as lojas</Typography>
        )}

        {outOfDeliveryArea ? (
          <OutOfDeliveryAreaPanel radiusKm={deliveryRadiusKm} />
        ) : (
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'stretch', gap: 2 }}>
            {providers.map((provider) => (
              <Box key={provider.id}>
                <ProviderCard
                  provider={provider}
                  onCardClick={() => navigate(`/provider/${provider.id}`)}
                  showProducts={false}
                  categories={true}
                  showBanner={true}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box >
  );
};

export default Home;
